(function () {
  "use strict";

  var ANNUAL_PRICE = 390;

  function formatCurrency(value) {
    var hasCents = Math.round(value * 100) % 100 !== 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0
    }).format(value);
  }

  function currentDateStamp() {
    var now = new Date();
    return {
      display: new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      }).format(now),
      compact: [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0")
      ].join("")
    };
  }

  function calculateEstimate(licenses, years) {
    var normalizedLicenses = Math.max(1, Math.min(1000, Math.round(Number(licenses) || 1)));
    var normalizedYears = Math.max(1, Math.min(5, Math.round(Number(years) || 1)));
    var volumeDiscount = normalizedLicenses >= 11 ? 10 : normalizedLicenses >= 5 ? 7 : 0;
    var multiYearDiscount = Math.min((normalizedYears - 1) * 10, 20);
    var discountRate = volumeDiscount + multiYearDiscount;
    var subtotal = normalizedLicenses * ANNUAL_PRICE * normalizedYears;
    var discountAmount = subtotal * (discountRate / 100);
    return {
      licenses: normalizedLicenses,
      years: normalizedYears,
      volumeDiscount: volumeDiscount,
      multiYearDiscount: multiYearDiscount,
      discountRate: discountRate,
      subtotal: subtotal,
      discountAmount: discountAmount,
      total: subtotal - discountAmount
    };
  }

  function setText(root, selector, value) {
    var element = root.querySelector(selector);
    if (element) element.textContent = value;
  }

  function setOptionalText(root, selector, value) {
    var element = root.querySelector(selector);
    if (!element) return;
    element.textContent = value;
    element.hidden = !value;
  }

  function initEstimator(root) {
    if (root.dataset.impactEstimatorReady === "true") return;
    root.dataset.impactEstimatorReady = "true";

    var organization = root.querySelector("[data-estimate-organization]");
    var contactName = root.querySelector("[data-estimate-contact-name]");
    var contactEmail = root.querySelector("[data-estimate-contact-email]");
    var address = root.querySelector("[data-estimate-address]");
    var licensesInput = root.querySelector("[data-estimate-licenses]");
    var yearsInput = root.querySelector("[data-estimate-years]");
    var emailButton = root.querySelector("[data-estimate-email]");
    var printButton = root.querySelector("[data-estimate-print]");
    var lastStamp = { display: "Current date", compact: "PENDING" };

    function getValues() {
      var licenses = Math.max(1, Math.min(1000, Math.round(Number(licensesInput.value) || 1)));
      var years = Math.max(1, Math.min(5, Math.round(Number(yearsInput.value) || 1)));
      licensesInput.value = String(licenses);
      yearsInput.value = String(years);
      return {
        organization: organization.value.trim(),
        contactName: contactName.value.trim(),
        contactEmail: contactEmail.value.trim(),
        address: address.value.trim(),
        licenses: licenses,
        years: years,
        estimate: calculateEstimate(licenses, years)
      };
    }

    function update(stamp) {
      if (stamp) lastStamp = stamp;
      var values = getValues();
      var estimate = values.estimate;
      var term = values.years + " " + (values.years === 1 ? "year" : "years");
      var reference = "WEB-" + lastStamp.compact + "-" + values.licenses + "L-" + values.years + "Y";

      setOptionalText(root, "[data-estimate-prepared]", values.organization ? "Prepared for " + values.organization : "");
      setText(root, "[data-estimate-total]", formatCurrency(estimate.total));
      setOptionalText(root, "[data-estimate-discount]", estimate.discountRate ? estimate.discountRate + "% total discount applied" : "");
      setText(root, "[data-estimate-license-count]", String(values.licenses));
      setText(root, "[data-estimate-term]", term);
      setText(root, "[data-estimate-subtotal]", formatCurrency(estimate.subtotal));
      setText(root, "[data-estimate-volume]", estimate.volumeDiscount + "%");
      setText(root, "[data-estimate-multiyear]", estimate.multiYearDiscount + "%");
      setText(root, "[data-estimate-savings]", "-" + formatCurrency(estimate.discountAmount));

      setText(root, "[data-doc-reference]", reference);
      setText(root, "[data-doc-date]", lastStamp.display);
      setText(root, "[data-doc-total]", formatCurrency(estimate.total));
      setText(root, "[data-doc-organization]", values.organization || "School or district");
      setOptionalText(root, "[data-doc-contact-name]", values.contactName);
      setOptionalText(root, "[data-doc-address]", values.address);
      setOptionalText(root, "[data-doc-contact-email]", values.contactEmail);
      setText(root, "[data-doc-licenses]", String(values.licenses));
      setText(root, "[data-doc-term]", term);
      setText(root, "[data-doc-subtotal]", formatCurrency(estimate.subtotal));
      setText(root, "[data-doc-list-subtotal]", formatCurrency(estimate.subtotal));
      setText(root, "[data-doc-volume-rate]", String(estimate.volumeDiscount));
      setText(root, "[data-doc-volume-amount]", "-" + formatCurrency(estimate.subtotal * (estimate.volumeDiscount / 100)));
      setText(root, "[data-doc-multiyear-rate]", String(estimate.multiYearDiscount));
      setText(root, "[data-doc-multiyear-amount]", "-" + formatCurrency(estimate.subtotal * (estimate.multiYearDiscount / 100)));
      setText(root, "[data-doc-total-usd]", formatCurrency(estimate.total) + " USD");
      setText(root, "[data-doc-notes]", "This estimate includes " + values.licenses + " fully transferable " + (values.licenses === 1 ? "license" : "licenses") + " for " + term + ". The purchasing school or district owns the " + (values.licenses === 1 ? "license" : "licenses") + " and may reassign " + (values.licenses === 1 ? "it" : "them") + " as staffing needs change.");
      return values;
    }

    function buildEstimateText(stamp) {
      var values = update(stamp);
      var estimate = values.estimate;
      return [
        "IMPACT School Counseling Curriculum planning estimate",
        values.organization ? "School or district: " + values.organization : null,
        values.contactName ? "Contact: " + values.contactName : null,
        values.contactEmail ? "Email: " + values.contactEmail : null,
        values.address ? "Billing address: " + values.address : null,
        "Estimate date: " + stamp.display,
        "Estimate reference: WEB-" + stamp.compact + "-" + values.licenses + "L-" + values.years + "Y",
        "Annual counselor licenses: " + values.licenses,
        "Access term: " + values.years + " " + (values.years === 1 ? "year" : "years"),
        "List price: " + formatCurrency(ANNUAL_PRICE) + " per counselor per year",
        "List-price subtotal: " + formatCurrency(estimate.subtotal),
        "Volume discount: " + estimate.volumeDiscount + "%",
        "Multi-year discount: " + estimate.multiYearDiscount + "%",
        "Total discount: " + estimate.discountRate + "%",
        "Estimated savings: " + formatCurrency(estimate.discountAmount),
        "Estimated total: " + formatCurrency(estimate.total),
        "This estimate is based on current list pricing and the discounts shown. Contact Bright Futures Counseling with any questions or special purchasing requirements."
      ].filter(Boolean).join("\n");
    }

    [organization, contactName, contactEmail, address, licensesInput, yearsInput].forEach(function (field) {
      field.addEventListener("input", function () { update(); });
      field.addEventListener("change", function () { update(); });
    });

    emailButton.addEventListener("click", function () {
      var stamp = currentDateStamp();
      var values = update(stamp);
      if (!values.contactEmail || !contactEmail.checkValidity()) {
        contactEmail.setCustomValidity("Enter a valid email address to email this estimate.");
        contactEmail.reportValidity();
        contactEmail.focus();
        return;
      }
      contactEmail.setCustomValidity("");
      var text = buildEstimateText(stamp);
      var subject = "IMPACT estimate" + (values.organization ? " for " + values.organization : "");
      window.location.href = "mailto:" + values.contactEmail + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(text);
    });

    contactEmail.addEventListener("input", function () { contactEmail.setCustomValidity(""); });

    printButton.addEventListener("click", function () {
      update(currentDateStamp());
      window.setTimeout(function () { window.print(); }, 0);
    });

    update();
  }

  function initMotion(scope) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    document.documentElement.classList.add("motion-ready");

    var revealElements = Array.prototype.slice.call(scope.querySelectorAll(".motion-reveal:not([data-motion-ready])"));
    var statElements = Array.prototype.slice.call(scope.querySelectorAll("[data-count]:not([data-count-ready])"));

    revealElements.forEach(function (element) {
      element.dataset.motionReady = "true";
      var delay = Number(element.getAttribute("data-reveal-delay") || 0);
      element.style.setProperty("--reveal-delay", delay + "ms");
    });

    if ("IntersectionObserver" in window) {
      var revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.14, rootMargin: "0px 0px -8%" });
      revealElements.forEach(function (element) { revealObserver.observe(element); });

      var countObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var element = entry.target;
          var target = Number(element.getAttribute("data-count") || 0);
          var suffix = element.getAttribute("data-suffix") || "";
          var startedAt = window.performance.now();
          var formatter = new Intl.NumberFormat("en-US");
          function tick(now) {
            var progress = Math.min((now - startedAt) / 1200, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            element.textContent = formatter.format(Math.round(target * eased)) + suffix;
            if (progress < 1) window.requestAnimationFrame(tick);
          }
          element.textContent = "0" + suffix;
          window.requestAnimationFrame(tick);
          countObserver.unobserve(element);
        });
      }, { threshold: 0.6 });

      statElements.forEach(function (element) {
        element.dataset.countReady = "true";
        countObserver.observe(element);
      });
    } else {
      revealElements.forEach(function (element) { element.classList.add("is-visible"); });
    }
  }

  function initVideoCard(card) {
    if (card.dataset.impactVideoReady === "true") return;
    card.dataset.impactVideoReady = "true";

    var button = card.querySelector("[data-video-play]");
    if (!button) return;

    button.addEventListener("click", function () {
      var provider = card.getAttribute("data-video-provider") || "youtube";
      var videoId = card.getAttribute("data-video-id") || "kTM1844aE2Q";
      var iframe = document.createElement("iframe");
      var source = provider === "vimeo"
        ? "https://player.vimeo.com/video/" + encodeURIComponent(videoId) + "?autoplay=1"
        : "https://www.youtube-nocookie.com/embed/" + encodeURIComponent(videoId) + "?autoplay=1&rel=0";

      iframe.src = source;
      iframe.title = "Take a tour of IMPACT";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      iframe.allow = provider === "vimeo"
        ? "autoplay; fullscreen; picture-in-picture"
        : "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.setAttribute("allowfullscreen", "");

      card.replaceChildren(iframe);
      iframe.focus();
    });
  }

  function initMobileMenu(header) {
    if (header.dataset.mobileMenuReady === "true") return;
    var toggle = header.querySelector("[data-mobile-menu-toggle]");
    var menu = header.querySelector("[data-mobile-menu]");
    if (!toggle || !menu) return;
    header.dataset.mobileMenuReady = "true";

    function closeMenu(returnFocus) {
      menu.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      toggle.querySelector(".visually-hidden").textContent = "Open menu";
      if (returnFocus) toggle.focus();
    }

    toggle.addEventListener("click", function () {
      var willOpen = toggle.getAttribute("aria-expanded") !== "true";
      menu.classList.toggle("is-open", willOpen);
      toggle.setAttribute("aria-expanded", String(willOpen));
      toggle.querySelector(".visually-hidden").textContent = willOpen ? "Close menu" : "Open menu";
    });

    menu.addEventListener("click", function (event) {
      if (event.target.closest("a")) closeMenu(false);
    });

    document.addEventListener("click", function (event) {
      if (!header.contains(event.target)) closeMenu(false);
    });

    header.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && menu.classList.contains("is-open")) closeMenu(true);
    });
  }

  function init(scope) {
    initMotion(scope);
    Array.prototype.slice.call(scope.querySelectorAll(".site-header")).forEach(initMobileMenu);
    Array.prototype.slice.call(scope.querySelectorAll("[data-impact-estimator]")).forEach(initEstimator);
    Array.prototype.slice.call(scope.querySelectorAll("[data-impact-video]")).forEach(initVideoCard);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) { init(event.target); });
})();
